// Una web en modo Settings, como en Framer (ficha 05): barra superior con Preview y Publish, lista de secciones a la
// izquierda (Site settings y Hosting) y el contenido en el centro.
import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Play } from 'lucide-react';
import { api, type Site } from '../../api.ts';
import { t } from '../../i18n/index.ts';
import { navigate } from '../../router.ts';
import { errorText, useApp } from '../../store.ts';
import { TopBar } from '../../ui/TopBar.tsx';
import { markViewed } from '../Dashboard.tsx';
import { General } from './General.tsx';
import { Versions } from './Versions.tsx';
import { Domains } from './Domains.tsx';
import { Rules } from './Rules.tsx';
import { Forms } from './Forms.tsx';

const SECTIONS: { group: string; items: { id: string; label: string }[] }[] = [
  {
    group: 'site.groupSite',
    items: [
      { id: 'general', label: 'site.general' },
      { id: 'versions', label: 'site.versions' },
      { id: 'forms', label: 'site.forms' },
    ],
  },
  {
    group: 'site.groupHosting',
    items: [
      { id: 'domains', label: 'site.domains' },
      { id: 'redirects', label: 'site.redirects' },
      { id: 'headers', label: 'site.headers' },
    ],
  },
];

export interface SiteProps {
  site: Site;
  onChange: (s: Site) => void;
  reload: () => Promise<void>;
  kind?: 'redirects' | 'headers';
}

export function SiteShell({ id, section }: { id: string; section: string }) {
  const putSite = useApp((s) => s.putSite);
  const notify = useApp((s) => s.notify);
  const [site, setSite] = useState<Site | null>(null);
  const [missing, setMissing] = useState(false);
  const [stagingId, setStagingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const { site: s } = await api.site(id);
      setSite(s);
      putSite(s);
      const { releases } = await api.releases(id);
      setStagingId(releases.find((r) => r.staging)?.id ?? null);
    } catch {
      setMissing(true);
    }
  }, [id, putSite]);

  useEffect(() => {
    markViewed(id);
    void reload();
  }, [id, reload]);

  const onChange = useCallback(
    (s: Site) => {
      setSite(s);
      putSite(s);
      void api.releases(s.id).then(({ releases }) => setStagingId(releases.find((r) => r.staging)?.id ?? null));
    },
    [putSite],
  );

  if (missing)
    return (
      <div className="login">
        <div className="login-card">
          <h1>{t('site.notFound')}</h1>
          <button type="button" className="btn" onClick={() => navigate('/')}>
            {t('common.goToDashboard')}
          </button>
        </div>
      </div>
    );
  if (!site) return null;

  const canPublish = Boolean(stagingId && stagingId !== site.published?.id && !site.archived);
  const publish = async () => {
    if (!stagingId) return;
    try {
      onChange((await api.publishRelease(stagingId)).site);
      notify(t('site.publishedToast', { url: site.hosts.url }));
    } catch (e) {
      notify(errorText(e), true);
    }
  };

  const Section = { general: General, versions: Versions, domains: Domains, redirects: Rules, headers: Rules, forms: Forms }[section] ?? General;

  return (
    <div className="settings">
      <TopBar
        mode={t('mode.settings')}
        menu={[
          { label: t('common.goToDashboard'), onSelect: () => navigate('/') },
          { separator: true },
          { label: t('site.openPreview'), onSelect: () => window.open(site.hosts.previewUrl, '_blank', 'noopener'), disabled: !stagingId },
          { label: t('site.openSite'), shortcut: 'Ctrl+Shift+O', onSelect: () => window.open(site.hosts.url, '_blank', 'noopener'), disabled: !site.published },
        ]}
        center={
          <>
            <span className="ellipsis" title={site.name}>
              {site.name}
            </span>
            {site.archived ? <span className="badge">{t('projects.archived')}</span> : null}
          </>
        }
        right={
          <>
            <button type="button" className="btn icon" style={{ background: 'var(--bg-selected)' }} title={t('site.preview')} disabled={!stagingId} onClick={() => window.open(site.hosts.previewUrl, '_blank', 'noopener')}>
              <Play size={14} color="var(--text)" />
            </button>
            <button type="button" className="btn" disabled={!site.published} onClick={() => window.open(site.hosts.url, '_blank', 'noopener')} title={site.hosts.url}>
              <ExternalLink size={12} />
              {t('site.visit')}
            </button>
            <button type="button" className="btn primary" disabled={!canPublish} onClick={publish} title={canPublish ? t('site.publishHint') : t('site.upToDate')}>
              {t('site.publish')}
            </button>
          </>
        }
      />
      <div className="settings-body">
        <nav className="settings-nav">
          {SECTIONS.map((g) => (
            <div key={g.group}>
              <div className="group">{t(g.group)}</div>
              {g.items.map((it) => (
                <button key={it.id} type="button" className={`side-item${section === it.id ? ' active' : ''}`} onClick={() => navigate(`/sites/${site.id}/${it.id}`)}>
                  <span>{t(it.label)}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <main className="settings-main">
          <div className="settings-page">
            <Section key={section} site={site} onChange={onChange} reload={reload} kind={section === 'headers' ? 'headers' : 'redirects'} />
          </div>
        </main>
      </div>
    </div>
  );
}
